import apiClient from '@/lib/api/apiClient'
import { throwServiceError, unwrapArrayPayload } from './serviceHelpers'

export async function getCreditBureauConfig(): Promise<{ active?: string } | null> {
  try {
    const response = await apiClient.get<{ active?: string }>('/api/config/cb_active')
    return response.data ?? null
  } catch (error) {
    throwServiceError(error, 'Failed to load credit bureau config.')
  }
}

/** Raw API body — may be an array or `{ addresses, last_used_ra_id, … }` (Angular parity). */
export async function getCreditReportAddressRequest(
  applicantId: string | number,
): Promise<unknown> {
  try {
    const response = await apiClient.get<unknown>(
      `/api/credit_report_request/${applicantId}`,
    )
    return response.data
  } catch (error) {
    throwServiceError(error, 'Failed to load credit report addresses.')
  }
}

export async function regenerateCreditReport(
  applicantId: string | number,
  params: Record<string, string | undefined>,
): Promise<void> {
  try {
    await apiClient.get(`/api/regenerate_credit_report/${applicantId}`, { params })
  } catch (error) {
    throwServiceError(error, 'Failed to regenerate credit report.')
  }
}

export async function retryFailedCreditJob(jobId: string | number): Promise<void> {
  try {
    await apiClient.put(`/api/failed_jobs/${jobId}`)
  } catch (error) {
    throwServiceError(error, 'Failed to retry credit job.')
  }
}

export async function updateApplicantCreditScore(
  applicantId: string | number,
  body: Record<string, string | number | undefined>,
): Promise<void> {
  try {
    const payload = Object.fromEntries(
      Object.entries(body).filter(([, v]) => v !== undefined),
    ) as Record<string, string | number>
    await apiClient.post(`/api/update_score/${applicantId}`, payload)
  } catch (error) {
    throwServiceError(error, 'Failed to save applicant score.')
  }
}

/** Update Ninja/Equifax score — POST /api/update_ninja_equifax_score/:id (Angular updateNinjaEquifaxScore). */
export async function updateNinjaEquifaxScore(
  applicantId: string | number,
  body: { equifax_score?: string | number; ninja_score?: string | number },
): Promise<void> {
  try {
    const payload = Object.fromEntries(
      Object.entries(body).filter(([, v]) => v !== undefined && v !== ''),
    ) as Record<string, string | number>
    await apiClient.post(`/api/update_ninja_equifax_score/${applicantId}`, payload)
  } catch (error) {
    throwServiceError(error, 'Failed to update score')
  }
}

/** Angular `application-details.component.ts` `loadninjaaccounts()` → `GET /api/ninja/list/:id`. */
export async function fetchNinjaListForApplicant(
  applicantId: string | number,
): Promise<unknown[]> {
  try {
    const response = await apiClient.get<unknown>(`/api/ninja/list/${applicantId}`)
    return unwrapArrayPayload(response.data)
  } catch {
    return []
  }
}
